import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { api, trackPageview } from "../lib/api";
import { ISLAND_BY_SLUG } from "../lib/constants";
import { useT } from "../lib/LangContext";

// Renders either a react-router <Link to={to}> (default in-app navigation)
// or a plain <a href target="_blank"> when `external` is true. Used by the
// PA hub which links spotlight cards directly to the publisher URL.
const CardLink = ({ external, to, href, onClick, children, ...rest }) => {
  if (external) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        onClick={onClick}
        {...rest}
      >
        {children}
      </a>
    );
  }
  return (
    <Link to={to} onClick={onClick} {...rest}>
      {children}
    </Link>
  );
};

// Business & Tourism Spotlight — premium editorial real estate that
// sits between the Hero CTA Strip and the main article feed grid.
// - Fetches the 5 most recent Business or Tourism stories (filtered to
//   the current island when applicable) from the existing /api/articles
//   endpoint via the newly-added comma-separated category filter.
// - Renders 1 featured story on the left (desktop) + a 2x2 grid of 4
//   supporting stories on the right. Mobile stacks to single column.
// - Hides completely when no matching articles exist.
// PA-hub uses `externalLinkOnly` mode: article cards link straight to the
// original publisher URL instead of the synthetic /article/:id detail page
// (which doesn't render PA-native content and would default the visitor
// back to their EN/NL/ES localStorage lang). Same pattern as HeroCarousel.
export const SpotlightSection = ({ island = "all", ads = {}, externalLinkOnly = false }) => {
  const [articles, setArticles] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const { t, lang, timeAgo } = useT();

  useEffect(() => {
    let cancelled = false;
    const params = { category: "Business,Tourism", limit: 5, lang };
    if (island !== "all") params.island = island;
    api
      .get("/articles", { params })
      .then((r) => {
        if (cancelled) return;
        setArticles(Array.isArray(r.data) ? r.data.slice(0, 5) : []);
        setLoaded(true);
      })
      .catch(() => {
        if (!cancelled) setLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, [island, lang]);

  // Hide the entire section when no articles exist (spec — no placeholder)
  if (!loaded || articles.length === 0) return null;

  const featured = articles[0];
  const supporting = articles.slice(1, 5);

  const sponsor = ads?.spotlight_sponsor;
  const sponsorName = sponsor?.active && sponsor?.sponsor_name ? sponsor.sponsor_name : "";
  return (
    <section
      className="mb-8"
      data-testid="spotlight-section"
      aria-labelledby="spotlight-heading"
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2.5">
          <span
            aria-hidden="true"
            style={{ width: 3, height: 20, background: "#0AADA0", display: "inline-block" }}
          />
          <span
            id="spotlight-heading"
            className="uppercase text-[#0AADA0]"
            style={{ fontSize: 11, letterSpacing: "0.08em", fontWeight: 600 }}
          >
            {t("spotlight_header")}          </span>
        </div>
        {sponsorName && (
          <span
            className="text-[#6B7C82]"
            style={{ fontSize: 10 }}
            data-testid="spotlight-sponsor-label"
          >
            {t("spotlight_sponsored_by")} {sponsorName}
          </span>
        )}
      </div>
      <div className="border-t border-[#EEEEEE] mb-5" aria-hidden="true" />

      {/* Layout: 40% featured + 60% 2x2 grid on desktop; single column mobile */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">
        <div className="lg:col-span-2">
          <FeaturedCard article={featured} timeAgo={timeAgo} externalLinkOnly={externalLinkOnly} />
        </div>
        <div className="lg:col-span-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {supporting.map((a) => (
              <SupportingCard key={a.id} article={a} timeAgo={timeAgo} externalLinkOnly={externalLinkOnly} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

const CategoryBadge = ({ category }) => {
  const { tCategory } = useT();
  const isTourism = category === "Tourism";
  const bg = isTourism ? "#0AADA0" : "#5DBB63";
  return (
    <span
      className="inline-block text-white uppercase"
      style={{
        background: bg,
        fontSize: 10,
        letterSpacing: "0.05em",
        padding: "3px 8px",
        borderRadius: 999,
        fontWeight: 600,
      }}
      data-testid={`spotlight-badge-${(category || "").toLowerCase()}`}
    >
      {tCategory(category)}
    </span>
  );
};

const FeaturedCard = ({ article, timeAgo, externalLinkOnly = false }) => {
  const { t } = useT();
  const [imgFailed, setImgFailed] = useState(false);
  const hasImage = !!article.image_url && !imgFailed;
  const islandMeta = ISLAND_BY_SLUG[article.island];
  const to = `/article/${article.id}${article.slug ? `-${article.slug}` : ""}`;
  const href = article.url;
  const linkTracker = () =>
    trackPageview(externalLinkOnly ? "/article/external" : "/article", article.id);
  const facts = Array.isArray(article.key_facts) ? article.key_facts.slice(0, 2) : [];

  return (
    <article
      className="bg-white border border-[#EEEEEE] flex flex-col h-full overflow-hidden"
      style={{ borderRadius: 12 }}
      data-testid="spotlight-featured"
    >
      <CardLink
        external={externalLinkOnly}
        to={to}
        href={href}
        onClick={linkTracker}
        data-testid="spotlight-featured-link"
      >
        {hasImage ? (
          <img
            src={article.image_url}
            alt={article.title}
            onError={() => setImgFailed(true)}
            className="w-full object-cover"
            style={{ height: 280, borderRadius: "12px 12px 0 0" }}
            loading="lazy"
          />
        ) : (
          <div
            className="w-full"
            style={{
              height: 280,
              background: "linear-gradient(135deg,#0AADA0 0%,#0AADA0cc 60%,#0AADA099 100%)",
              borderRadius: "12px 12px 0 0",
            }}
          />
        )}
      </CardLink>
      <div className="p-4 flex-1 flex flex-col">
        <div className="mb-2">
          <CategoryBadge category={article.category} />
        </div>
        <CardLink external={externalLinkOnly} to={to} href={href} onClick={linkTracker}>
          <h3
            className="font-serif-display text-[#1A2E35] mb-2"
            style={{
              fontSize: 20,
              lineHeight: 1.3,
              display: "-webkit-box",
              WebkitLineClamp: 3,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
            data-testid="spotlight-featured-title"
          >
            {article.title}
          </h3>
        </CardLink>
        {article.ai_summary && (
          <p
            className="text-[#6B7C82] mb-3"
            style={{
              fontSize: 13,
              lineHeight: 1.6,
              display: "-webkit-box",
              WebkitLineClamp: 3,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {article.ai_summary}
          </p>
        )}
        {facts.length > 0 && (
          <ul className="mb-3 space-y-1">
            {facts.map((f, i) => (
              <li
                key={i}
                className="flex items-start gap-2 text-[#6B7C82]"
                style={{ fontSize: 12 }}
              >
                <span
                  aria-hidden="true"
                  style={{
                    width: 6,
                    height: 6,
                    borderRadius: 999,
                    background: "#0AADA0",
                    marginTop: 6,
                    flexShrink: 0,
                  }}
                />
                <span>{f}</span>
              </li>
            ))}
          </ul>
        )}
        <div
          className="flex items-center gap-2 text-[#6B7C82] mb-3"
          style={{ fontSize: 11 }}
        >
          <span className="truncate">{article.source_name}</span>
          {islandMeta && <span>{islandMeta.flag}</span>}
          <span>·</span>
          <span>{timeAgo(article.published_at)}</span>
        </div>
        <div className="mt-auto">
          <CardLink
            external={externalLinkOnly}
            to={to}
            href={href}
            onClick={linkTracker}
            className="inline-flex items-center gap-1.5 bg-[#E63946] hover:bg-[#cf2f3c] text-white transition-colors"
            style={{
              fontSize: 13,
              padding: "8px 14px",
              borderRadius: 999,
              fontWeight: 600,
            }}
            data-testid="spotlight-featured-cta"
          >
            {t("spotlight_read_full")} <ArrowRight size={14} />
          </CardLink>
        </div>
      </div>
    </article>
  );
};

const SupportingCard = ({ article, timeAgo, externalLinkOnly = false }) => {
  const [imgFailed, setImgFailed] = useState(false);
  const hasImage = !!article.image_url && !imgFailed;
  const to = `/article/${article.id}${article.slug ? `-${article.slug}` : ""}`;
  const href = article.url;
  const linkTracker = () =>
    trackPageview(externalLinkOnly ? "/article/external" : "/article", article.id);
  return (
    <CardLink
      external={externalLinkOnly}
      to={to}
      href={href}
      onClick={linkTracker}
      className="bg-white border border-[#EEEEEE] flex items-start gap-3 transition-shadow hover:shadow-md"
      style={{ borderRadius: 8, padding: 10, transition: "box-shadow 0.2s" }}
      data-testid={`spotlight-supporting-${article.id}`}
    >
      {hasImage ? (
        <img
          src={article.image_url}
          alt={article.title}
          onError={() => setImgFailed(true)}
          className="object-cover flex-shrink-0"
          style={{ width: 100, height: 80, borderRadius: 8 }}
          loading="lazy"
        />
      ) : (
        <div
          className="flex-shrink-0"
          style={{
            width: 100,
            height: 80,
            borderRadius: 8,
            background: "linear-gradient(135deg,#0AADA0 0%,#0AADA0cc 100%)",
          }}
        />
      )}
      <div className="flex-1 min-w-0">
        <div className="mb-1.5">
          <CategoryBadge category={article.category} />
        </div>
        <h4
          className="text-[#1A2E35] mb-1"
          style={{
            fontSize: 13,
            fontWeight: 500,
            lineHeight: 1.35,
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {article.title}
        </h4>
        <div
          className="flex items-center gap-1.5 text-[#6B7C82] truncate"
          style={{ fontSize: 11 }}
        >
          <span className="truncate">{article.source_name}</span>
          <span>·</span>
          <span className="flex-shrink-0">{timeAgo(article.published_at)}</span>
        </div>
      </div>
    </CardLink>
  );
};
